import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { RequireAuth, RequirePermission } from './components/guards';
import { PageLoader } from './components/ui';
import { UpdateBanner } from './components/UpdateBanner';

// Route-level code splitting: the office map pulls in PixiJS, which has no
// business being in the bundle for someone who only opens the HR dashboard.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'));
const PublicDeckPage = lazy(() => import('./pages/PublicDeckPage'));
const OfficePage = lazy(() => import('./pages/OfficePage'));
const OraPage = lazy(() => import('./pages/OraPage'));
const DirectoryPage = lazy(() => import('./pages/DirectoryPage'));
const MyDayPage = lazy(() => import('./pages/MyDayPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const LeavePage = lazy(() => import('./pages/LeavePage'));
const TeamLeavePage = lazy(() => import('./pages/TeamLeavePage'));
const TeamDashboardPage = lazy(() => import('./pages/TeamDashboardPage'));
const HrDashboardPage = lazy(() => import('./pages/HrDashboardPage'));
const HrReportsPage = lazy(() => import('./pages/HrReportsPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminMapPage = lazy(() => import('./pages/AdminMapPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'));
const AdminAuditPage = lazy(() => import('./pages/AdminAuditPage'));
const AdminStoragePage = lazy(() => import('./pages/AdminStoragePage'));
const VaultPage = lazy(() => import('./pages/VaultPage'));
const VaultEditorPage = lazy(() => import('./pages/VaultEditorPage'));
const VaultStudioPage = lazy(() => import('./pages/VaultStudioPage'));
const DeckEditorPage = lazy(() => import('./pages/DeckEditorPage'));
const DocEditorPage = lazy(() => import('./pages/DocEditorPage'));
const SheetEditorPage = lazy(() => import('./pages/SheetEditorPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

export default function App() {
  return (
    <>
      <UpdateBanner />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* A presentation shared with "Anyone with the link": no sign-in. */}
        <Route path="/p/:token" element={<PublicDeckPage />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Navigate to="/office" replace />} />
          <Route path="/office" element={<OfficePage />} />
          <Route path="/assistant" element={<OraPage />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/people/:userId" element={<ProfilePage />} />
          <Route path="/my-day" element={<MyDayPage />} />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:conversationId" element={<ChatPage />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/leave" element={<LeavePage />} />
          <Route path="/files" element={<VaultPage />} />
          <Route path="/files/folder/:folderId" element={<VaultPage />} />
          <Route path="/files/shared" element={<VaultPage />} />
          <Route path="/files/recent" element={<VaultPage />} />
          <Route path="/files/search" element={<VaultPage />} />
          <Route path="/files/trash" element={<VaultPage />} />
          <Route path="/files/edit/:fileId" element={<VaultEditorPage />} />
          <Route path="/files/create" element={<VaultStudioPage />} />
          <Route path="/files/create/:fileId" element={<VaultStudioPage />} />
          <Route path="/files/deck/:fileId" element={<DeckEditorPage />} />
          <Route path="/files/doc/:fileId" element={<DocEditorPage />} />
          <Route path="/files/sheet/:fileId" element={<SheetEditorPage />} />
          {/* Files used to be called the Vault: old links, bookmarks and notifications still land. */}
          <Route path="/vault/*" element={<VaultRedirect />} />
          <Route path="/settings/integrations" element={<IntegrationsPage />} />

          <Route
            path="/team"
            element={
              <RequirePermission permission="team:read">
                <TeamDashboardPage />
              </RequirePermission>
            }
          />
          <Route
            path="/team/leave"
            element={
              <RequirePermission permission="leave:approve:team">
                <TeamLeavePage />
              </RequirePermission>
            }
          />
          <Route
            path="/hr"
            element={
              <RequirePermission permission="report:read:org">
                <HrDashboardPage />
              </RequirePermission>
            }
          />
          <Route
            path="/hr/reports"
            element={
              <RequirePermission permission="report:read:org">
                <HrReportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequirePermission permission="user:manage">
                <AdminUsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/map"
            element={
              <RequirePermission permission="map:manage">
                <AdminMapPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequirePermission permission={['integration:manage', 'leave:policy:manage']}>
                <AdminSettingsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/audit"
            element={
              <RequirePermission permission="audit:read">
                <AdminAuditPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/storage"
            element={
              <RequirePermission permission="user:manage">
                <AdminStoragePage />
              </RequirePermission>
            }
          />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </>
  );
}

/** `/vault/...?x` → `/files/...?x`, keeping the rest of the address. */
function VaultRedirect() {
  const location = useLocation();
  return <Navigate to={`${location.pathname.replace(/^\/vault/, '/files')}${location.search}${location.hash}`} replace />;
}
